import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useStore from "../../../context/useStore";
import tools from "../../../helpers/tools";
import { ACCUEIL_OPERATEUR } from "../../../helpers/perimetreOperateur";
import { estAdministrateurOperateur, estUtilisateurOperateur } from "../../../helpers/rolesManager";
import Control from "../../common/Control";
import ModaleLocale from "../../common/CustomModal/ModaleLocale";
import { EmptyState, Pill } from "../../common/DataList";
import { FactList, FicheHeader, FicheSection, StickySaveBar } from "../../common/Fiche";
import Loader from "../../common/Loader";
import CartesRolesCompte from "./CartesRolesCompte";
import CaseAlertesPlanning from "./CaseAlertesPlanning";
import ChoixSitesCompte from "./ChoixSitesCompte";
import DroitsProfil from "./DroitsProfil";
import {
  changerActivationCompteOperateur,
  listerComptesDeOperateur,
  listerSitesDeOperateur,
  modifierCompteOperateur,
  renvoyerLienActivationCompteOperateur,
  voirOperateur,
} from "./operateursApi";
import {
  ROLES_COMPTE_OPERATEUR,
  champsVerrouilles,
  corpsModification,
  estDesactive,
  estEnAttente,
  estModifieCompte,
  etatCompteOperateur,
  formulaireDepuisCompte,
  libelleRoleCompte,
  nomCompletCompte,
  peutDesactiver,
  rolesProposes,
  validerFormulaireCompte,
} from "./comptesOperateur";
import "./operateurs.scss";

// ============================================================
// Fiche d'un compte d'opérateur (CE-1407, maquette 10), page dédiée
// /operateurs/:id/comptes/:compteId, sur le kit Fiche : en-tête d'identité
// (état, rôle, alertes ; renvoyer le lien, désactiver ou activer), faits,
// sections Identité, Rôle dans la structure (cartes de rôle, option alertes,
// encadré des droits) et Sites, barre d'enregistrement collante.
// La Caf modifie tout ; l'administrateur d'opérateur ne fixe ni l'adresse ni
// la date de fin de contrat, ne nomme pas d'administrateur et ne se désactive
// pas lui-même ; un manageur ou un agent d'opérateur lit la fiche. Un compte
// d'opérateur ne voit que sa structure : une autre est refusée sans appel.
// L'API n'a pas de lecture d'un compte seul : la fiche le prend dans la liste
// des comptes de la structure (quelques dizaines au plus).
// ============================================================

const CIVILITES = [
  { id: 1, name: "Monsieur" },
  { id: 2, name: "Madame" },
];

const dateEtHeure = (valeur) => (valeur ? tools.formatDate(valeur, "dd/MM/yyyy 'à' HH:mm") : "");

const ChampLecture = ({ champ, label, valeur }) => (
  <div className="form-group my-0">
    <label>{label}</label>
    <p className="champ-lecture" data-champ={champ}>
      <span>{valeur || <span className="fact__empty">Non renseigné</span>}</span>
      <i className="fa fa-lock" aria-hidden="true" title="Fixé par la Caf" />
      <span className="sr-only">Fixé par la Caf</span>
    </p>
  </div>
);

const PageVide = ({ titre, aide, retour, libelleRetour, action = null }) => (
  <div className="container-fluid px-5 my-5 fiche-page">
    <section className="list-card">
      <EmptyState icon="fa-user" title={titre} hint={aide} action={action} erreur />
      <p className="text-center pb-4 mb-0">
        <Link to={retour} className="btn btn-ghost">
          {libelleRetour}
        </Link>
      </p>
    </section>
  </div>
);

const FicheCompteOperateur = (props) => {
  const [state] = useStore();
  const utilisateurConnecte = state.auth && state.auth.user;
  const editeur = useMemo(() => utilisateurConnecte || {}, [utilisateurConnecte]);
  const { id: operateurId, compteId } = (props.match && props.match.params) || {};
  const vueOperateur = estUtilisateurOperateur(editeur);
  const structureEtrangere = vueOperateur && editeur.operateur !== operateurId;
  const retour = vueOperateur ? `${ACCUEIL_OPERATEUR}/utilisateurs` : `/operateurs/${operateurId}`;

  const [operateur, setOperateur] = useState(null);
  const [compte, setCompte] = useState(null);
  const [sites, setSites] = useState([]);
  const [chargement, setChargement] = useState({ fait: false, erreur: null, introuvable: false });
  const [formulaire, setFormulaire] = useState(null);
  const [reference, setReference] = useState(null);
  const [erreurs, setErreurs] = useState({});
  const [refus, setRefus] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [actionEnCours, setActionEnCours] = useState(false);
  const [confirmationDesactivation, setConfirmationDesactivation] = useState(false);

  const poserCompte = (fiche) => {
    const valeurs = formulaireDepuisCompte(fiche);
    setCompte(fiche);
    setFormulaire(valeurs);
    setReference(valeurs);
  };

  const charger = () => {
    setChargement({ fait: false, erreur: null, introuvable: false });
    Promise.all([voirOperateur(operateurId), listerComptesDeOperateur(operateurId), listerSitesDeOperateur(operateurId)]).then(
      ([fiche, comptes, sitesStructure]) => {
        setOperateur(fiche);
        setSites(sitesStructure || []);
        const trouve = (comptes || []).find((c) => c.id === compteId);
        if (trouve) poserCompte(trouve);
        setChargement({ fait: true, erreur: null, introuvable: !trouve });
      },
      (erreur) =>
        setChargement({
          fait: true,
          erreur: (erreur && erreur.message) || "Une erreur est survenue",
          introuvable: Boolean(erreur && erreur.statut === 404),
        })
    );
  };

  useEffect(() => {
    if (structureEtrangere || !operateurId) return;
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operateurId, compteId, structureEtrangere]);

  const verrous = useMemo(() => champsVerrouilles({ editeur, compte }), [editeur, compte]);
  const modifie = Boolean(formulaire && reference) && estModifieCompte(formulaire, reference);

  if (structureEtrangere) {
    return (
      <PageVide
        titre="Cette structure n'est pas la vôtre"
        aide="Votre compte ne voit que sa structure, ses sites et ses comptes."
        retour={ACCUEIL_OPERATEUR}
        libelleRetour="Retour à ma structure"
      />
    );
  }
  if (!chargement.fait) return <Loader />;
  if (chargement.erreur && !chargement.introuvable) {
    return (
      <PageVide
        titre="La fiche du compte n'a pas pu être chargée"
        aide={chargement.erreur}
        retour={retour}
        libelleRetour="Retour aux utilisateurs"
        action={{ label: "Réessayer", onClick: charger }}
      />
    );
  }
  if (chargement.introuvable || !compte) {
    return (
      <PageVide
        titre="Ce compte n'existe pas"
        aide="Il a peut-être été supprimé, ou il appartient à une autre structure."
        retour={retour}
        libelleRetour="Retour aux utilisateurs"
      />
    );
  }

  const peutAgir = !vueOperateur || estAdministrateurOperateur(editeur.role);
  const lectureSeule = !peutAgir;
  const nom = nomCompletCompte(compte);
  const etat = etatCompteOperateur(compte);

  const changer = (e) => {
    const { name, value, type, checked } = e.target;
    modifierChamp(name, type === "checkbox" ? checked : value);
  };
  const modifierChamp = (name, valeur) => {
    setFormulaire((f) => ({ ...f, [name]: valeur }));
    setErreurs((er) => ({ ...er, [name]: undefined }));
    setRefus(null);
  };

  const annuler = () => {
    setFormulaire(reference);
    setErreurs({});
    setRefus(null);
  };

  const enregistrer = () => {
    const invalides = validerFormulaireCompte(formulaire, { nombreSites: sites.length, verrous });
    if (Object.keys(invalides).length) {
      setErreurs(invalides);
      return;
    }
    setEnregistrement(true);
    setErreurs({});
    setRefus(null);
    modifierCompteOperateur(operateurId, compte.id, corpsModification(formulaire, { editeur, compte })).then(
      (fiche) => {
        setEnregistrement(false);
        poserCompte(fiche);
        toast.success(`Le compte de ${nomCompletCompte(fiche)} a été enregistré.`);
      },
      (erreur) => {
        setEnregistrement(false);
        if (erreur.champs) setErreurs(erreur.champs);
        else setRefus(erreur.message || "L'enregistrement a été refusé.");
      }
    );
  };

  const changerActivation = (actif) => {
    setActionEnCours(true);
    changerActivationCompteOperateur(operateurId, compte.id, actif).then(
      (fiche) => {
        setActionEnCours(false);
        setConfirmationDesactivation(false);
        setCompte(fiche);
        toast.success(`Le compte de ${nom} est ${actif ? "réactivé" : "désactivé"}.`);
      },
      (erreur) => {
        setActionEnCours(false);
        setConfirmationDesactivation(false);
        toast.error(erreur.message || (actif ? "La réactivation a été refusée." : "La désactivation a été refusée."));
      }
    );
  };

  const renvoyer = () => {
    setActionEnCours(true);
    renvoyerLienActivationCompteOperateur(operateurId, compte.id).then(
      (fiche) => {
        setActionEnCours(false);
        if (fiche) setCompte(fiche);
        toast.success(`L'e-mail d'activation a été renvoyé à ${compte.email}.`);
      },
      (erreur) => {
        setActionEnCours(false);
        toast.error(erreur.message || "Le renvoi de l'e-mail d'activation a été refusé.");
      }
    );
  };

  const actions = [];
  if (peutAgir) {
    if (estEnAttente(compte)) {
      actions.push(
        <button key="renvoyer" type="button" className="btn btn-ghost" onClick={renvoyer} disabled={actionEnCours}>
          <i className="fa fa-envelope-open-text mr-2" aria-hidden="true" />
          Renvoyer l'e-mail d'activation
        </button>
      );
    }
    if (estDesactive(compte)) {
      actions.push(
        <button key="activer" type="button" className="btn btn-ghost" onClick={() => changerActivation(true)} disabled={actionEnCours}>
          <i className="fa fa-power-off mr-2" aria-hidden="true" />
          Activer le compte
        </button>
      );
    } else if (compte.estActive && peutDesactiver({ editeur, compte })) {
      actions.push(
        <button key="desactiver" type="button" className="btn btn-ghost" onClick={() => setConfirmationDesactivation(true)} disabled={actionEnCours}>
          <i className="fa fa-power-off mr-2" aria-hidden="true" />
          Désactiver le compte
        </button>
      );
    }
  }

  const faits = [
    { id: "email", label: "E-mail", value: compte.email, truncate: true },
    { id: "telephone", label: "Téléphone", value: compte.telephone, mono: true },
    { id: "structure", label: "Structure", value: operateur.nom },
    { id: "derniereConnexion", label: "Dernière connexion", value: dateEtHeure(compte.derniereConnexionLe) || "Jamais" },
    { id: "creeLe", label: "Compte créé le", value: compte.creeLe ? tools.formatDate(compte.creeLe) : "" },
    { id: "finContrat", label: "Fin de contrat", value: compte.dateFinContrat ? tools.formatDate(compte.dateFinContrat) : "" },
  ];

  const roleCourant = formulaire.role;
  const descriptionRole = (ROLES_COMPTE_OPERATEUR.find((r) => r.id === compte.role) || {}).description;

  return (
    <div className="container-fluid px-5 my-5 fiche-page fiche-operateur fiche-compte-operateur">
      <FicheHeader
        back={retour}
        backLabel={`Retour aux utilisateurs de ${operateur.nom}`}
        eyebrow={`Utilisateur · ${operateur.nom}`}
        title={nom}
        pills={
          <>
            <Pill tone={etat.ton} icon={etat.icone}>
              {etat.libelle}
            </Pill>
            <Pill tone="dark" icon="fa-id-badge">
              {libelleRoleCompte(compte.role)}
            </Pill>
            {compte.recoitAlertesPlanning && (
              <Pill tone="info" icon="fa-bell">
                Alertes de planning
              </Pill>
            )}
          </>
        }
        actions={actions.length ? actions : null}
      >
        <FactList facts={faits} emptyLabel="Non renseigné" />
      </FicheHeader>

      {refus && (
        <div className="alert alert-danger mt-3" role="alert">
          {refus}
        </div>
      )}

      <div className="fiche-compte-operateur__grille">
        {!lectureSeule && (
          <FicheSection id="fiche-compte-identite" title="Identité">
            <div className="fiche-operateur__champs">
              <Control
                margin="my-0"
                label="Civilité"
                type="select"
                name="civilite"
                value={formulaire.civilite}
                datas={CIVILITES}
                dataIndex="id"
                dataLabel="name"
                noSelectFirstOption
                change={changer}
                error={erreurs}
              />
              <div className="modale-operateur__vide" aria-hidden="true" />
              <Control margin="my-0" label="Prénom" name="prenom" value={formulaire.prenom} change={changer} error={erreurs} required inputProps={{ autoComplete: "off" }} />
              <Control margin="my-0" label="Nom" name="nom" value={formulaire.nom} change={changer} error={erreurs} required inputProps={{ autoComplete: "off" }} />
              {verrous.includes("email") ? (
                <ChampLecture champ="email" label="E-mail" valeur={formulaire.email} />
              ) : (
                <Control
                  margin="my-0"
                  label="E-mail"
                  type="email"
                  name="email"
                  value={formulaire.email}
                  change={changer}
                  error={erreurs}
                  required
                  inputProps={{ autoComplete: "off", inputMode: "email" }}
                />
              )}
              <Control
                margin="my-0"
                label="Téléphone"
                type="tel"
                name="telephone"
                value={formulaire.telephone}
                change={changer}
                error={erreurs}
                inputProps={{ inputMode: "tel" }}
              />
              {verrous.includes("dateFinContrat") ? (
                <ChampLecture
                  champ="dateFinContrat"
                  label="Date de fin de contrat"
                  valeur={formulaire.dateFinContrat ? formulaire.dateFinContrat.split("-").reverse().join("/") : ""}
                />
              ) : (
                <div>
                  <Control
                    margin="my-0"
                    label="Date de fin de contrat"
                    type="date"
                    name="dateFinContrat"
                    value={formulaire.dateFinContrat}
                    change={changer}
                    error={erreurs}
                  />
                  <p className="modale-operateur__aide">Le compte ne peut plus se connecter après cette date.</p>
                </div>
              )}
            </div>
          </FicheSection>
        )}

        <FicheSection id="fiche-compte-role" title="Rôle dans la structure">
          {lectureSeule ? (
            <div className="fiche-compte-operateur__role">
              <p className="choix-role__titre mb-0">{libelleRoleCompte(compte.role)}</p>
              {descriptionRole && <p className="choix-role__description">{descriptionRole}</p>}
              <p className="fiche-operateur__secondaire mb-0">
                {compte.recoitAlertesPlanning ? "Reçoit les alertes de planning." : "Ne reçoit pas les alertes de planning."}
              </p>
            </div>
          ) : (
            <>
              <CartesRolesCompte
                roles={rolesProposes({ editeur })}
                valeur={roleCourant}
                onChange={(role) => modifierChamp("role", role)}
                verrouille={verrous.includes("role")}
                erreur={erreurs.role}
              />
              <div className="modale-operateur__alertes">
                <CaseAlertesPlanning
                  coche={formulaire.recoitAlertesPlanning}
                  onChange={(valeur) => modifierChamp("recoitAlertesPlanning", valeur)}
                />
              </div>
            </>
          )}
          <DroitsProfil regimes={operateur.regimes} role={lectureSeule ? compte.role : roleCourant} nomStructure={operateur.nom} />
        </FicheSection>

        <FicheSection id="fiche-compte-sites" title="Sites" className="fiche-compte-operateur__pleine-largeur">
          <ChoixSitesCompte
            sites={sites}
            selection={lectureSeule ? compte.sites || [] : formulaire.sites}
            onChange={(ids) => modifierChamp("sites", ids)}
            nomStructure={operateur.nom}
            erreur={erreurs.sites}
            lectureSeule={lectureSeule}
            vueLimitee={vueOperateur}
            idPrefix="fiche-compte"
            avecLibelle={false}
          />
        </FicheSection>
      </div>

      {!lectureSeule && (
        <StickySaveBar
          visible={modifie}
          isSaving={enregistrement}
          onSave={enregistrer}
          onCancel={annuler}
          message="Modifications non enregistrées"
          label="Enregistrer"
        />
      )}

      <ModaleLocale
        ouvert={confirmationDesactivation}
        onFermer={() => setConfirmationDesactivation(false)}
        titre={`Désactiver le compte de ${nom} ?`}
        taille="etroite"
        idTitre="modale-desactivation-compte"
        pied={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmationDesactivation(false)}>
              Annuler
            </button>
            <button type="button" className="btn btn-primary" onClick={() => changerActivation(false)} disabled={actionEnCours}>
              Désactiver le compte
            </button>
          </>
        }
      >
        <p className="mb-0">
          Le compte ne pourra plus se connecter. Il reste dans la liste des comptes désactivés et peut être réactivé.
        </p>
      </ModaleLocale>
    </div>
  );
};

export default FicheCompteOperateur;
